import React from 'react';
import { array, bool, object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import {
  IconReviewStar,
  IconSpinner,
  LayoutSingleColumn,
  ListingCard,
  NamedLink,
  Page,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { formatMoney, unitDivisor } from '../../util/currency';
import { useIntl } from '../../util/reactIntl';
import { createSlug } from '../../util/urlHelpers';
import balloonGarlandHeroCollage from '../../assets/balloon-garland-hero-collage.png';

import {
  portCreditErrorSelector,
  portCreditInProgressSelector,
  portCreditListingsSelector,
} from './SeoLocationPage.duck';
import {
  DEFAULT_SEO_LOCATION_PAGE_ID,
  getSeoLocationPageConfig,
} from './SeoLocationPage.config';

import css from './SeoLocationPage.module.css';

const searchPagePath = pageConfig => pageConfig.browseUrl || `/s?${pageConfig.searchQuery}`;

const getImageSources = listing => {
  const variants = listing.images?.[0]?.attributes?.variants || {};
  const seoCard = variants['seo-card']?.url;
  const seoCard2x = variants['seo-card-2x']?.url;
  const listingCard = variants['listing-card']?.url;
  const listingCard2x = variants['listing-card-2x']?.url;
  const fallback =
    seoCard ||
    seoCard2x ||
    listingCard ||
    listingCard2x ||
    variants['scaled-medium']?.url ||
    variants['scaled-small']?.url ||
    Object.values(variants)[0]?.url;
  const srcSet = seoCard && seoCard2x ? `${seoCard} 248w, ${seoCard2x} 496w` : null;

  return {
    src: fallback,
    srcSet,
  };
};

const getImageUrl = listing => getImageSources(listing).src;

const productCardImageSizes =
  '(min-width: 1280px) 248px, (min-width: 1024px) calc((100vw - 120px) / 4), (min-width: 768px) calc((100vw - 96px) / 3), (min-width: 550px) calc((100vw - 72px) / 2), calc(100vw - 48px)';

const imageVariantToMetaImage = (variant, name, defaultWidth, defaultHeight) =>
  variant?.url
    ? {
        name,
        url: variant.url,
        width: variant.width || defaultWidth,
        height: variant.height || defaultHeight,
      }
    : null;

const findListingImageVariant = (listings, variantName) => {
  const listingWithVariant = listings.find(listing => {
    const variants = listing.images?.[0]?.attributes?.variants || {};
    return variants[variantName]?.url;
  });

  return listingWithVariant?.images?.[0]?.attributes?.variants?.[variantName] || null;
};

const buildSocialSharing = (listings, pageConfig) => {
  const facebookVariant =
    findListingImageVariant(listings, 'facebook') ||
    findListingImageVariant(listings, 'seo-card-2x');
  const twitterVariant =
    findListingImageVariant(listings, 'twitter') ||
    findListingImageVariant(listings, 'seo-card');
  const facebookImage = imageVariantToMetaImage(facebookVariant, 'facebook', 1200, 630);
  const twitterImage = imageVariantToMetaImage(twitterVariant, 'twitter', 600, 314);

  return {
    title: pageConfig.title,
    description: pageConfig.description,
    ...(facebookImage ? { images1200: [facebookImage] } : {}),
    ...(twitterImage ? { images600: [twitterImage] } : {}),
  };
};

const listingUrl = (rootUrl, listing) => {
  const id = listing.id.uuid;
  const slug = createSlug(listing.attributes.title || 'listing');
  return `${rootUrl}/l/${slug}/${id}`;
};

const productSchema = (listing, rootUrl) => {
  const { title, price, publicData = {} } = listing.attributes;
  const ratingValue = publicData.aggregateRating ? publicData.aggregateRating / 100 : null;
  const reviewCount = publicData.numberOfRatings;
  const image = getImageUrl(listing);
  const url = listingUrl(rootUrl, listing);

  return {
    '@type': 'Product',
    name: title,
    url,
    ...(image ? { image: [image] } : {}),
    brand: {
      '@type': 'Brand',
      name: 'LocalFlower',
    },
    ...(price
      ? {
          offers: {
            '@type': 'Offer',
            url,
            price: (price.amount / unitDivisor(price.currency)).toFixed(2),
            priceCurrency: price.currency,
            availability: 'https://schema.org/InStock',
          },
        }
      : {}),
    ...(ratingValue && reviewCount
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue,
            reviewCount,
          },
        }
      : {}),
  };
};

const listingRatingData = listing => {
  const { aggregateRating, numberOfRatings } = listing.attributes.publicData || {};
  const ratingValue = aggregateRating ? aggregateRating / 100 : null;
  const reviewCount = numberOfRatings ? Number(numberOfRatings) : 0;

  return reviewCount > 0 ? { ratingValue, reviewCount } : null;
};

const buildBreadcrumbSchema = (rootUrl, pageConfig) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { name: 'Home', href: rootUrl },
    ...pageConfig.breadcrumbs.map(item => ({
      name: item.name,
      href: item.href ? `${rootUrl}${item.href}` : `${rootUrl}${pageConfig.path}`,
    })),
  ].map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.href,
    })),
});

const buildSchema = (listings, rootUrl, pageConfig) => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageConfig.title,
    description: pageConfig.description,
    url: `${rootUrl}${pageConfig.path}`,
  },
  buildBreadcrumbSchema(rootUrl, pageConfig),
  ...(pageConfig.faqItems && pageConfig.faqItems.length > 0
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: pageConfig.faqItems.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        },
      ]
    : []),
  ...(listings.length > 0
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: pageConfig.itemListName,
          itemListElement: listings.map((listing, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: productSchema(listing, rootUrl),
          })),
        },
      ]
    : []),
];

const SeoListingCard = props => {
  const { listing, intl, index } = props;
  const { title = '', price } = listing.attributes;
  const authorName = listing.author?.attributes?.profile?.displayName;
  const image = getImageSources(listing);
  const id = listing.id.uuid;
  const slug = createSlug(title);
  const formattedPrice = price ? formatMoney(intl, price) : null;
  const ratingData = listingRatingData(listing);

  return (
    <NamedLink className={css.listingCard} name="ListingPage" params={{ id, slug }}>
      <div className={css.listingImageWrap}>
        {image.src ? (
          <img
            className={css.listingImage}
            src={image.src}
            srcSet={image.srcSet || undefined}
            sizes={image.srcSet ? productCardImageSizes : undefined}
            alt={title}
            loading={index === 0 ? 'eager' : 'lazy'}
            decoding="async"
            width="248"
            height="248"
          />
        ) : null}
      </div>
      <div className={css.listingInfo}>
        <div className={css.listingMetaRow}>
          {formattedPrice ? <div className={css.listingPrice}>{formattedPrice}</div> : null}
          {ratingData ? (
            <div className={css.listingRating} aria-label={`${ratingData.reviewCount} reviews`}>
              <IconReviewStar className={css.listingRatingStar} isFilled />
              {ratingData.ratingValue ? <span>{ratingData.ratingValue}</span> : null}
              <span>({ratingData.reviewCount})</span>
            </div>
          ) : null}
        </div>
        <h3 className={css.listingTitle}>{title}</h3>
        {authorName ? <p className={css.listingAuthor}>{authorName}</p> : null}
      </div>
    </NamedLink>
  );
};

const ProductGrid = props => {
  const { listings, inProgress, error, intl, layout } = props;
  const hasFade = layout === 'twoColumnFade';
  const gridClassName =
    layout === 'twoColumnFade'
      ? `${css.productGrid} ${css.productGridTwoColumns}`
      : css.productGrid;

  if (inProgress && listings.length === 0) {
    return (
      <div className={css.loading}>
        <IconSpinner />
      </div>
    );
  }

  if (error && listings.length === 0) {
    return (
      <p className={css.error}>
        We could not load bouquets right now. Please try again or browse all flowers.
      </p>
    );
  }

  const grid = (
    <div className={gridClassName}>
      {listings.map((listing, index) =>
        hasFade ? (
          <div className={css.productListingCardWrap} key={listing.id.uuid}>
            <ListingCard
              listing={listing}
              className={css.productListingCard}
              renderSizes="(min-width: 1280px) 248px, (min-width: 1024px) calc((100vw - 220px) / 4), (min-width: 768px) calc((100vw - 120px) / 2), calc((100vw - 92px) / 2)"
              showAuthorInfo={false}
            />
          </div>
        ) : (
          <SeoListingCard key={listing.id.uuid} listing={listing} intl={intl} index={index} />
        )
      )}
    </div>
  );

  return hasFade ? (
    <div className={css.productGridFrame}>
      {grid}
      <div className={css.productGridFade} aria-hidden="true" />
    </div>
  ) : (
    grid
  );
};

const LinkList = props => {
  const { title, description, links } = props;

  return (
    <section className={css.linkSection}>
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
      <div className={css.linkGrid}>
        {links.map(link => (
          <a key={link.label} href={link.href} className={css.internalLink}>
            {link.label}
          </a>
        ))}
      </div>
    </section>
  );
};

export const SeoLocationPageComponent = props => {
  const { listings, inProgress, error, scrollingDisabled, seoPageId } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const pageConfig = getSeoLocationPageConfig(seoPageId || DEFAULT_SEO_LOCATION_PAGE_ID);
  const hideProducts = pageConfig.hideProducts === true;
  const currentSearchPagePath = hideProducts ? null : searchPagePath(pageConfig);
  const primaryCtaHref = pageConfig.primaryCtaHref || currentSearchPagePath || '#';
  const secondaryCtaHref = pageConfig.secondaryCtaHref || (pageConfig.faqId ? `#${pageConfig.faqId}` : '#');
  const schema = buildSchema(listings, config.marketplaceRootURL, pageConfig);
  const socialSharing = buildSocialSharing(listings, pageConfig);
  const hasProductShowcase = !hideProducts && pageConfig.productsLayout === 'twoColumnFade';
  const heroClassName = hasProductShowcase ? `${css.hero} ${css.heroCompact}` : css.hero;
  const productSectionClassName = hasProductShowcase
    ? `${css.productsSection} ${css.productsSectionShowcase}`
    : css.productsSection;
  const trustStrip = (
    <section
      className={
        hasProductShowcase ? `${css.trustStrip} ${css.trustStripAfterShowcase}` : css.trustStrip
      }
      aria-label="LocalFlower benefits"
    >
      {pageConfig.trustItems.map(item => (
        <div key={item.title}>
          <strong>{item.title}</strong>
          <span>{item.text}</span>
        </div>
      ))}
    </section>
  );
  const productsContent = (
    <>
      {hasProductShowcase ? null : (
        <div className={css.sectionHeader}>
          <p className={css.eyebrow}>{pageConfig.productEyebrow}</p>
          <h2>{pageConfig.productsTitle}</h2>
          <p>{pageConfig.productsDescription}</p>
        </div>
      )}
      <ProductGrid
        listings={listings}
        inProgress={inProgress}
        error={error}
        intl={intl}
        layout={pageConfig.productsLayout}
      />
      {hasProductShowcase ? (
        <div className={css.productsFooter}>
          {pageConfig.productsTitle ? (
            <h2 className={css.productsFooterTitle}>{pageConfig.productsTitle}</h2>
          ) : null}
          {pageConfig.productsDescription ? (
            <p className={css.productsFooterText}>{pageConfig.productsDescription}</p>
          ) : null}
          <a className={css.moreProductsLink} href={currentSearchPagePath}>
            {pageConfig.moreProductsLabel}
          </a>
        </div>
      ) : (
        <a className={css.moreProductsLink} href={currentSearchPagePath}>
          {pageConfig.moreProductsLabel}
        </a>
      )}
    </>
  );

  return (
    <Page
      title={pageConfig.title}
      description={pageConfig.description}
      socialSharing={socialSharing}
      schema={schema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <section className={heroClassName}>
            <div className={css.heroText}>
              <nav className={css.breadcrumbs} aria-label="Breadcrumb">
                <NamedLink name="LandingPage">Home</NamedLink>
                {pageConfig.breadcrumbs.map(item => (
                  <React.Fragment key={item.name}>
                    <span aria-hidden="true">/</span>
                    {item.href ? <a href={item.href}>{item.name}</a> : <span>{item.name}</span>}
                  </React.Fragment>
                ))}
              </nav>
              <p className={css.eyebrow}>{pageConfig.eyebrow}</p>
              <h1 className={css.heroTitle}>{pageConfig.heroTitle}</h1>
              <p className={css.heroDescription}>{pageConfig.heroDescription}</p>
              <div className={css.heroActions}>
                <a className={css.primaryCta} href={primaryCtaHref}>
                  {pageConfig.primaryCtaLabel}
                </a>
                {pageConfig.secondaryCtaLabel ? (
                  <a className={css.secondaryCta} href={secondaryCtaHref}>
                    {pageConfig.secondaryCtaLabel}
                  </a>
                ) : null}
              </div>
            </div>
            {hasProductShowcase ? (
              <div className={css.heroCollagePanel}>
                <img
                  className={css.heroCollageImage}
                  src={pageConfig.heroCollage?.src || balloonGarlandHeroCollage}
                  alt={pageConfig.heroCollage?.alt || 'Balloon garland decor examples'}
                  loading="eager"
                  decoding="async"
                />
              </div>
            ) : (
              <div className={css.heroCard}>
                <span className={css.heroCardLabel}>{pageConfig.heroCardLabel}</span>
                <strong>{pageConfig.heroCardTitle}</strong>
                <p>{pageConfig.heroCardDescription}</p>
              </div>
            )}
          </section>

          {hasProductShowcase ? null : trustStrip}

          {hideProducts ? null : (
            <section className={productSectionClassName}>
              {hasProductShowcase ? (
                <div className={css.productsSectionCard}>{productsContent}</div>
              ) : (
                productsContent
              )}
            </section>
          )}

          {hasProductShowcase ? trustStrip : null}

          {pageConfig.linkSections.map(section => {
            const links = section.links.filter(link => link.href !== pageConfig.path);

            return links.length > 0 ? (
              <LinkList
                key={section.title}
                title={section.title}
                description={section.description}
                links={links}
              />
            ) : null;
          })}

          <section className={css.contentGrid}>
            {pageConfig.contentArticles.map(article => (
              <article key={article.title}>
                <h2>{article.title}</h2>
                <p>{article.text}</p>
              </article>
            ))}
          </section>

          {pageConfig.faqItems && pageConfig.faqItems.length > 0 ? (
            <section id={pageConfig.faqId} className={css.faqSection}>
              <div className={css.sectionHeader}>
                <p className={css.eyebrow}>Questions</p>
                <h2>{pageConfig.faqTitle}</h2>
              </div>
              <div className={css.faqGrid}>
                {pageConfig.faqItems.map(item => (
                  <article key={item.question} className={css.faqItem}>
                    <h3>{item.question}</h3>
                    <p>{item.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoLocationPageComponent.propTypes = {
  listings: array,
  inProgress: bool,
  error: object,
  scrollingDisabled: bool,
  seoPageId: string,
};

const mapStateToProps = state => ({
  listings: portCreditListingsSelector(state),
  inProgress: portCreditInProgressSelector(state),
  error: portCreditErrorSelector(state),
  scrollingDisabled: isScrollingDisabled(state),
});

export default compose(connect(mapStateToProps))(SeoLocationPageComponent);
